import { useState, type FormEvent } from 'react';
import { api, ApiError } from '@/api/client';
import { getSession, setSession } from '@/auth/session';
import { Callout } from '@/components/Callout';
import { PageHeader } from '@/components/ops/Layout';

/** The server's rules, repeated so the admin learns them before submitting (the server re-checks). */
export const MIN_PASSWORD_LENGTH = 12;

/** Why the new password cannot be sent yet, or null. */
export function newPasswordProblem(current: string, next: string, confirm: string): string | null {
  if (next.length < MIN_PASSWORD_LENGTH) return `Use at least ${MIN_PASSWORD_LENGTH} characters.`;
  if (next === current) return 'Choose a password different from the current one.';
  if (next !== confirm) return 'The two new passwords are not the same.';
  return null;
}

/**
 * Change the signed-in admin's password. `forced`: the account must change it before using the console (a seeded or
 * reset account) — the server refuses every other call until it is done, so this screen replaces the whole console.
 * A change signs out every other session.
 */
export function ChangePasswordPage({ forced = false }: { forced?: boolean }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const problem = next || confirm ? newPasswordProblem(current, next, confirm) : null;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (newPasswordProblem(current, next, confirm)) return;
    setBusy(true);
    setError(null);
    try {
      setSession(await api.changePassword(current, next));
      setCurrent('');
      setNext('');
      setConfirm('');
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reach the control plane. Try again.');
    } finally {
      setBusy(false);
    }
  }

  const form = (
    <form className={forced ? 'card login-card' : 'card form-card'} onSubmit={submit}>
      {forced && (
        <>
          <h1 className="t-display" style={{ margin: '0 0 4px' }}>Choose your own password</h1>
          <p className="t-body muted" style={{ margin: '0 0 16px' }}>
            {getSession()?.user.display_name}, this account was set up with a password someone else chose. Pick your own
            before using the console.
          </p>
        </>
      )}
      {error && <div style={{ marginBottom: 16 }}><Callout tone="error">{error}</Callout></div>}
      {done && !forced && <div style={{ marginBottom: 16 }}><Callout tone="info">Password changed. Every other session was signed out.</Callout></div>}

      <div className="field">
        <label htmlFor="current-password">Current password</label>
        <input id="current-password" className="input" type="password" autoComplete="current-password" required
          value={current} onChange={(e) => setCurrent(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="new-password">New password</label>
        <input id="new-password" className="input" type="password" autoComplete="new-password" required
          minLength={MIN_PASSWORD_LENGTH} value={next} onChange={(e) => setNext(e.target.value)} aria-describedby="password-rules" />
        <span id="password-rules" className="t-caption muted">
          At least {MIN_PASSWORD_LENGTH} characters, not your current one and not your e-mail name. A short sentence works well.
        </span>
      </div>
      <div className="field">
        <label htmlFor="confirm-password">New password again</label>
        <input id="confirm-password" className="input" type="password" autoComplete="new-password" required
          value={confirm} onChange={(e) => setConfirm(e.target.value)} />
      </div>
      {problem && <p className="t-caption note-error" role="status" style={{ margin: '0 0 12px' }}>{problem}</p>}

      <div className="row">
        <button className="btn btn-primary" type="submit" disabled={busy || !current || !!problem || !next}>
          {busy ? <span className="spinner" aria-label="Saving" /> : 'Change password'}
        </button>
        {forced && (
          <button className="btn btn-ghost" type="button" onClick={() => void api.logout()}>Sign out</button>
        )}
      </div>
    </form>
  );

  if (forced) {
    return <div className="login-page">{form}</div>;
  }

  return (
    <div className="stack">
      <PageHeader title="Change password" subtitle="Changing it signs you out everywhere else." />
      {form}
    </div>
  );
}
